import React from 'react'
import { useTranslation } from 'react-i18next'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Tooltip from '@mui/material/Tooltip'
import Stack from '@mui/material/Stack'

import UserIcon from 'src/components/ui/UserIcon'
import {
  AppearancePreference,
  isAppearancePreference,
  useAppearance,
} from 'src/context/appearanceContext'

const OPTIONS: {
  value: AppearancePreference
  icon: string
  labelKey: 'appearanceLight' | 'appearanceDark' | 'appearanceSystem'
}[] = [
  { value: 'light', icon: 'tabler:sun', labelKey: 'appearanceLight' },
  { value: 'dark', icon: 'tabler:moon-stars', labelKey: 'appearanceDark' },
  {
    value: 'system',
    icon: 'tabler:device-desktop',
    labelKey: 'appearanceSystem',
  },
]

interface AppearanceToggleProps {
  size?: 'small' | 'medium'
  iconsOnly?: boolean
}

// Light / Dark / System selector. Used on the profile page and in the
// profile dropdown; the state itself lives in AppearanceProvider.
export const AppearanceToggle = ({
  size = 'medium',
  iconsOnly = false,
}: AppearanceToggleProps) => {
  const { t } = useTranslation('profile')
  const { preference, setPreference } = useAppearance()

  const handleChange = (_: React.MouseEvent, value: unknown) => {
    if (isAppearancePreference(value)) setPreference(value)
  }

  return (
    <ToggleButtonGroup
      exclusive
      size={size}
      value={preference}
      onChange={handleChange}
      aria-label={t('appearance')}
      sx={{
        '& .MuiToggleButton-root': {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: size === 'small' ? 12 : 13,
          lineHeight: 1.2,
          px: iconsOnly ? 1.5 : 2.5,
          py: size === 'small' ? 0.75 : 1,
          color: 'text.secondary',
          borderColor: 'var(--app-glass-border)',
          backgroundColor: 'var(--app-button-surface)',
          transition:
            'background-color 160ms ease, background 160ms ease, box-shadow 160ms ease, border-color 160ms ease, color 160ms ease',
          '&:hover': {
            background: 'var(--app-menu-hover-bg)',
            color: 'var(--app-hover-color)',
          },
          '&.Mui-selected, &.Mui-selected:hover': {
            background: 'var(--app-menu-active-bg)',
            color: 'var(--app-menu-active-color)',
            borderColor: 'var(--app-menu-active-border)',
            boxShadow: 'var(--app-menu-active-shadow)',
          },
        },
      }}
    >
      {OPTIONS.map((option) => {
        const label = t(option.labelKey)
        const button = (
          <ToggleButton
            key={option.value}
            value={option.value}
            // ToggleButtonGroup derives `selected` from its direct child's
            // `value`; with the Tooltip wrapper that child is the Tooltip.
            selected={preference === option.value}
            aria-label={label}
          >
            <Stack direction="row" alignItems="center" columnGap={1}>
              <UserIcon
                icon={option.icon}
                fontSize={size === 'small' ? 16 : 18}
              />
              {!iconsOnly && <span>{label}</span>}
            </Stack>
          </ToggleButton>
        )

        return iconsOnly ? (
          <Tooltip key={option.value} title={label}>
            {button}
          </Tooltip>
        ) : (
          button
        )
      })}
    </ToggleButtonGroup>
  )
}

export default AppearanceToggle
